/**
 * Signature Pad Wrapper
 * Handles signature pad initialization
 */

export class SignaturePadWrapper {
  static translator = null;

  /**
   * Set translator instance
   * @param {Translator} translator - Translator instance
   */
  static setTranslator(translator) {
    this.translator = translator;
  }

  /**
   * Initialize signature pads
   * @param {Element} [container] - Form container (used to get correct document for iframes)
   */
  static init(container) {
    // Add click listener immediately so sign button works even if CDN script loads slowly/fails
    SignaturePadWrapper.setupSignatureButtons(container);

    const doc = (container && container.ownerDocument) || document;
    if (!window.SignaturePad) {
      const script = doc.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/signature_pad@4.0.9/dist/signature_pad.umd.min.js';
      script.onload = () => { /* library ready for modal canvas */ };
      (doc.head || doc.documentElement).appendChild(script);
    }
  }

  /**
   * Setup signature buttons (delegated click listener; one per document for iframe support)
   */
  static setupSignatureButtons(container) {
    const doc = (container && container.ownerDocument) || document;
    if (!SignaturePadWrapper._listenerDocs) SignaturePadWrapper._listenerDocs = new Set();
    if (SignaturePadWrapper._listenerDocs.has(doc)) return;
    SignaturePadWrapper._listenerDocs.add(doc);

    doc.addEventListener('click', async (e) => {
      const button = e.target && e.target.closest ? e.target.closest('.sign_me') : null;
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        await SignaturePadWrapper.handleSignatureClick(button);
      }
    }, true);
  }

  /**
   * Load SweetAlert2 if not available
   * @returns {Promise}
   */
  static async loadSweetAlert2() {
    if (window.Swal) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      // Check if already loading
      if (document.getElementById('sweetalert2-script')) {
        // Wait for existing load
        const checkInterval = setInterval(() => {
          if (window.Swal) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.Swal) {
            reject(new Error('SweetAlert2 failed to load'));
          }
        }, 5000);
        return;
      }

      // Load SweetAlert2 CSS
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://cdn.jsdelivr.net/npm/sweetalert2@11/dist/sweetalert2.min.css';
      document.head.appendChild(cssLink);

      // Load SweetAlert2 JS
      const script = document.createElement('script');
      script.id = 'sweetalert2-script';
      script.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11/dist/sweetalert2.min.js';
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load SweetAlert2'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Handle signature button click
   * @param {Element} button - Button element
   */
  static async handleSignatureClick(button) {
    const fieldId = button.getAttribute('data-id');
    let signaturePad;

    // Load SweetAlert2 if not available
    const t = this.translator ? (key) => this.translator.t(key) : ((key) => key);

    try {
      await this.loadSweetAlert2();
    } catch (error) {
      alert(t('signature.signature_required'));
      return;
    }

    if (!window.Swal) {
      alert(t('signature.signature_required'));
      return;
    }

    try {
      window.Swal.fire({
        title: t('signature.please_sign'),
        html: '<canvas id="signature-pad" width="300" height="150" style="border: 1px solid #000000;"></canvas>',
        showCancelButton: true,
        confirmButtonText: t('buttons.save'),
        cancelButtonText: t('buttons.cancel'),
        didOpen: () => {
          const canvas = document.getElementById('signature-pad');
          if (!window.SignaturePad) {
            window.Swal.showValidationMessage('Signature pad library loading, please try again.');
            return;
          }
          signaturePad = new window.SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)'
          });

          const cancelButton = window.Swal.getCancelButton();
          if (cancelButton) {
            cancelButton.addEventListener('click', () => {
              signaturePad.clear();
            });
          }
        },
        preConfirm: () => {
          if (signaturePad.isEmpty()) {
            window.Swal.showValidationMessage(t('signature.please_sign'));
            return false;
          }
          return signaturePad.toDataURL();
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          SignaturePadWrapper.confirmSignature(result.value, fieldId, button);
        }
      }).catch((err) => {
      });
    } catch (err) {
      alert(t('signature.signature_required'));
    }
  }

  /**
   * Confirm signature
   * @param {string} signatureData - Signature data URL
   * @param {string} fieldId - Field ID
   * @param {Element} button - Button element
   */
  static async confirmSignature(signatureData, fieldId, button) {
    const t = this.translator ? (key) => this.translator.t(key) : ((key) => key);
    
    if (window.Swal) {
      const confirmResult = await window.Swal.fire({
        title: t('signature.your_signature'),
        imageUrl: signatureData,
        imageAlt: t('signature.your_signature'),
        showCancelButton: true,
        confirmButtonText: t('buttons.confirm'),
        cancelButtonText: t('buttons.sign_again')
      });

      if (confirmResult.isConfirmed) {
        await this.uploadSignature(signatureData, fieldId, button);
      } else {
        // User wants to redo
        this.handleSignatureClick(button);
      }
    }
  }

  /**
   * Upload signature
   * @param {string} signatureData - Signature data URL
   * @param {string} fieldId - Field ID
   * @param {Element} button - Button element
   */
  static async uploadSignature(signatureData, fieldId, button) {
    try {
      const formData = new FormData();
      formData.append('useMimeType', 'png');
      formData.append('output', signatureData);
      formData.append('outputType', 'imagelink');

      const response = await fetch('/', {
        method: 'POST',
        body: formData
      });

      const uploadedData = await response.json();

      if (uploadedData && uploadedData.file) {
        const fieldInput = document.querySelector(`[name="${fieldId}"]`);
        if (fieldInput) {
          fieldInput.value = uploadedData.file;
        }

        const img = document.createElement('img');
        img.src = uploadedData.file;
        img.style.display = 'block';

        button.classList.add('d-none');
        if (button.nextSibling) {
          button.parentNode.insertBefore(img, button.nextSibling);
        } else {
          button.parentNode.appendChild(img);
        }

        document.body.dispatchEvent(new CustomEvent('tqtag-signature-completed', { detail: { fieldId } }));
      }
    } catch (error) {
      const t = this.translator ? (key) => this.translator.t(key) : ((key) => key);
      
      if (window.Swal) {
        window.Swal.fire({
          icon: 'error',
          title: t('signature.error'),
          text: t('signature.failed_to_upload')
        });
      }
    }
  }
}

